import React, { useEffect, useContext, useState, useMemo } from "react";
import { FirebaseAppContext } from "../context/FirebaseAppProvider";
import { getFirestore, collection, getDocs } from "firebase/firestore";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import styled from "styled-components";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

const StyledDiv = styled.div`
  padding: 20px;
`;

const COLUMNS = [
  { id: "name", label: "Name", align: "left" },
  { id: "email", label: "Email", align: "right" },
  { id: "signedAt", label: "Signed At", align: "right" },
];

// Waiver.signedAt is a Firestore Timestamp; everything else sorts as text.
const sortValue = (waiver, columnId) =>
  columnId === "signedAt"
    ? waiver.signedAt?.seconds ?? 0
    : (waiver[columnId] || "").toString().toLowerCase();

export const WaiverTable = () => {
  const firebaseApp = useContext(FirebaseAppContext);
  const db = getFirestore(firebaseApp);
  const [waivers, setWaivers] = useState([]);
  const [password, setPassword] = useState("");
  const [orderBy, setOrderBy] = useState(null);
  const [order, setOrder] = useState("asc");
  const [search, setSearch] = useState("");

  const [modalOpen, setModalOpen] = useState(true);

  const formattedDate = (date) => {
    return new Date(date.seconds * 1000).toLocaleString();
  };

  const handleSort = (columnId) => {
    if (orderBy === columnId) {
      setOrder(order === "asc" ? "desc" : "asc");
    } else {
      setOrderBy(columnId);
      setOrder("asc");
    }
  };

  const filteredWaivers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return waivers;
    return waivers.filter((waiver) => {
      const name = (waiver.name || "").toLowerCase();
      const email = (waiver.email || "").toLowerCase();
      const signedAt = waiver.signedAt
        ? formattedDate(waiver.signedAt).toLowerCase()
        : "";
      return (
        name.includes(query) ||
        email.includes(query) ||
        signedAt.includes(query)
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waivers, search]);

  const sortedWaivers = useMemo(() => {
    if (!orderBy) return filteredWaivers;
    const direction = order === "asc" ? 1 : -1;
    return [...filteredWaivers].sort((a, b) => {
      const aVal = sortValue(a, orderBy);
      const bVal = sortValue(b, orderBy);
      if (aVal < bVal) return -direction;
      if (aVal > bVal) return direction;
      return 0;
    });
  }, [filteredWaivers, order, orderBy]);

  const handleClick = () => {
    if (password === "uaps") {
      setModalOpen(false);
    }
  };

  useEffect(() => {
    const fetchWaivers = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "waivers"));
        const waivers = [];
        querySnapshot.forEach((doc) => {
          waivers.push({ id: doc.id, ...doc.data() });
        });
        console.log(waivers);
        setWaivers(waivers);
      } catch (error) {
        console.error("Error fetching waivers: ", error);
      }
    };

    fetchWaivers();
  }, [db]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Enter") {
        handleClick();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleClick]);

  return (
    <StyledDiv>
      <Typography variant="h4" align="center" sx={{ marginBottom: "20px" }}>
        Signed Waivers
      </Typography>
      <TextField
        label="Search"
        placeholder="Search by name, email, or signed date"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        fullWidth
        sx={{ marginBottom: "20px" }}
      />
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              {COLUMNS.map((column) => (
                <TableCell key={column.id} align={column.align}>
                  <TableSortLabel
                    active={orderBy === column.id}
                    direction={orderBy === column.id ? order : "asc"}
                    onClick={() => handleSort(column.id)}
                  >
                    <b>{column.label}</b>
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell align="right">
                <b>Waiver</b>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedWaivers.map((waiver) => (
              <TableRow
                key={waiver.id}
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
              >
                <TableCell component="th" scope="row">
                  {waiver.name}
                </TableCell>
                <TableCell align="right">{waiver.email}</TableCell>
                <TableCell align="right">
                  {formattedDate(waiver.signedAt)}
                </TableCell>
                <TableCell align="right">
                  <Button
                    variant="contained"
                    href={waiver.waiver}
                    target="_blank"
                  >
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modal */}
      <Modal
        open={modalOpen}
        onClose={() => {}}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backdropFilter: "blur(3px)",
        }}
        disableAutoFocus={true}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            border: "2px solid #000",
            boxShadow: 24,
            p: 4,
            display: "flex",
            flexDirection: "column",
            gap: "10px",
          }}
        >
          <TextField
            id="outlined-password-input"
            label="Password"
            type="password"
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
          />
          <Button variant="contained" onClick={handleClick}>
            Enter
          </Button>
        </Box>
      </Modal>
    </StyledDiv>
  );
};
